import React, { useEffect, useState } from 'react';
import { RefreshCw, TrendingUp, DollarSign, Percent } from 'lucide-react';
import api from '../../services/api';
import { dineroSeguro, pctSeguro } from '../../utils/pnlHistoricoFormat';
import { normalizarBriefDesdeHistorico } from '../../utils/kiqDataAdapter';
import { text, border, status, brand, radius, typography, tint } from '../design-system/tokens';

// KIQ Assistant — Fase 3 — Restaurant Brief. Reutiliza EXACTAMENTE el mismo
// endpoint ya usado por pages/PnLHistorico.js (GET /api/pnl/historico/:anio)
// — nunca un segundo cálculo de ventas/food cost/labor/prime cost/utilidad/
// margen. `kiqDataAdapter.js` solo normaliza la forma, nunca calcula.
//
// 4 estados explícitos (sección 9 del pedido): loading/ready/no_data/error.
// "no_data" es honesto: el año no tiene ningún mes cargado todavía — nunca
// se disfraza de "ready" con las 6 tarjetas en $0/0.0% inventado.
//
// Fase 3.1 — orden agrupado conceptualmente (sección 10 del pedido:
// BUSINESS / COST STRUCTURE / PROFITABILITY) SIN encabezados de grupo
// visibles: en una grilla de 2 columnas x 3 filas, este orden ya produce
// [Ventas | Utilidad Operativa] / [Food Cost | Labor] / [Prime Cost |
// Margen Operativo] — jerarquía por posición, no por texto nuevo. El
// ícono (DollarSign vs Percent) es puramente decorativo y usa el MISMO
// color neutral para las 6 tarjetas — nunca varía según el valor (sección
// 23: cero juicio de desempeño, ni siquiera implícito vía color).
const METRICAS = [
  { clave: 'ventas', etiqueta: 'Ventas', tipo: 'currency' },
  { clave: 'utilidad_operativa', etiqueta: 'Utilidad Operativa', tipo: 'currency' },
  { clave: 'food_cost_pct', etiqueta: 'Food Cost', tipo: 'percentage' },
  { clave: 'labor_pct', etiqueta: 'Labor', tipo: 'percentage' },
  { clave: 'prime_cost_pct', etiqueta: 'Prime Cost', tipo: 'percentage' },
  { clave: 'margen_operativo_pct', etiqueta: 'Margen Operativo', tipo: 'percentage' },
];

export default function KIQBrief() {
  const [estado, setEstado] = useState('loading'); // loading | ready | no_data | error
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  const cargar = async () => {
    setEstado('loading');
    setError(null);
    try {
      const anio = new Date().getFullYear();
      const { data } = await api.get(`/api/pnl/historico/${anio}`);
      const brief = normalizarBriefDesdeHistorico(data);
      setDatos(brief);
      setEstado(brief.tieneDatos ? 'ready' : 'no_data');
    } catch (err) {
      setError(err?.response?.data?.error || 'No se pudo cargar el resumen del restaurante.');
      setEstado('error');
    }
  };

  useEffect(() => { cargar(); }, []);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
        <TrendingUp size={14} color={text.muted} />
        <h2 style={{ ...typography.title, color: text.primary, margin: 0 }}>Resumen del restaurante</h2>
      </div>

      {estado === 'loading' && (
        <div style={{ ...typography.bodySmall, color: text.muted, padding: '9px 10px' }}>Cargando resumen…</div>
      )}

      {estado === 'error' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', border: `1px solid ${status.danger}40`, borderRadius: radius.md, background: `${status.danger}12` }}>
          <span style={{ ...typography.bodySmall, color: text.secondary }}>{error}</span>
          <button type="button" onClick={cargar} style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: '5px', background: 'var(--card-alt)', border: `1px solid ${border.default}`, borderRadius: radius.sm, padding: '5px 10px', fontSize: '12px', fontWeight: 700, color: text.secondary, cursor: 'pointer' }}>
            <RefreshCw size={12} /> Reintentar
          </button>
        </div>
      )}

      {estado === 'no_data' && (
        <div style={{ ...typography.bodySmall, color: text.muted, padding: '9px 10px' }}>
          Todavía no hay datos financieros cargados este año — KIQ mostrará tu resumen en cuanto exista al menos un mes registrado.
        </div>
      )}

      {estado === 'ready' && datos && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '8px' }}>
          {METRICAS.map((m) => {
            const valor = datos[m.clave];
            const formateado = m.tipo === 'currency' ? dineroSeguro(valor) : pctSeguro(valor);
            const Icono = m.tipo === 'currency' ? DollarSign : Percent;
            return (
              <div key={m.clave} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', border: `1px solid ${border.default}`, borderRadius: radius.md, padding: '10px', background: 'var(--card)' }}>
                <span style={{
                  width: '22px', height: '22px', flexShrink: 0, borderRadius: radius.sm, marginTop: '1px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: tint(brand.kiqBlue, 0.12), color: brand.kiqBlue,
                }}>
                  <Icono size={12} aria-hidden="true" />
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ ...typography.caption, color: text.muted, textTransform: 'none' }}>{m.etiqueta}</div>
                  <div style={{ fontFamily: typography.fontFamilyMono, fontSize: m.tipo === 'currency' ? '16px' : '15px', fontWeight: 800, color: text.primary, marginTop: '2px' }}>{formateado}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
